import type { ReactNode } from "react";
import { StyleSheet, Text } from "react-native";

import {
  fonts,
  typography,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type { TextSize, TextWeight } from "./types";

export function AppText({
  children,
  size = "sm",
  weight,
  tone = "default",
  selectable,
}: {
  children: ReactNode;
  size?: TextSize;
  weight?: TextWeight;
  tone?: "default" | "muted" | "danger" | "inverse" | "link";
  selectable?: boolean;
}) {
  const { stylesheet, sizeStyles, weightStyles } = useThemedStyles(makeStyles);
  return (
    <Text
      selectable={selectable}
      style={[
        sizeStyles[size],
        weight ? weightStyles[weight] : null,
        tone === "muted" ? stylesheet.muted : null,
        tone === "danger" ? stylesheet.danger : null,
        tone === "inverse" ? stylesheet.inverse : null,
        tone === "link" ? stylesheet.link : null,
      ]}
    >
      {children}
    </Text>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    xs: { ...typography.xs, color: colors.foreground },
    sm: { ...typography.small, color: colors.foreground },
    base: { ...typography.body, color: colors.foreground },
    lg: { ...typography.large, color: colors.foreground },
    display: { ...typography.display2, color: colors.heading },
    medium: { fontFamily: typography.label.fontFamily },
    semibold: { fontFamily: fonts.semibold },
    bold: { fontFamily: fonts.bold },
    muted: { color: colors.mutedForeground },
    danger: { color: colors.danger },
    inverse: { color: colors.primaryForeground },
    link: { color: colors.primary },
  });
  return {
    stylesheet,
    sizeStyles: {
      xs: stylesheet.xs,
      sm: stylesheet.sm,
      base: stylesheet.base,
      lg: stylesheet.lg,
      display: stylesheet.display,
    },
    weightStyles: {
      medium: stylesheet.medium,
      semibold: stylesheet.semibold,
      bold: stylesheet.bold,
    },
  };
};
